import React, { useEffect, useState, useCallback } from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import { onboardingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

import OnboardingOverlay from './OnboardingOverlay';
import TutorialControls from './TutorialControls';
import { useOnboardingStep } from './hooks/useOnboardingStep';
import { createPortal } from 'react-dom';
import TutorialDialog from './TutorialDialog';
import TutorialPopper from './TutorialPopper';
import { tabIndexToId, findElementBySelectors } from './utils/dom';

// Новый хук для управления шагами гайда
const useVisibleSteps = (
  activeGuide,
  checkStepRoleAccess,
  checkStepDisplayConditions
) => {
  const shouldShowStep = useCallback(
    (step) => {
      if (!step) return false;
      return checkStepRoleAccess(step) && checkStepDisplayConditions(step);
    },
    [checkStepRoleAccess, checkStepDisplayConditions]
  );

  const getVisibleSteps = useCallback(() => {
    if (!activeGuide || !activeGuide.steps) return [];
    return activeGuide.steps.filter(shouldShowStep);
  }, [activeGuide, shouldShowStep]);

  const visibleSteps = getVisibleSteps();
  return { visibleSteps, shouldShowStep, getVisibleSteps };
};

// Хук для управления ролями пользователя
const useUserRoles = (user) => {
  return useCallback(() => {
    return Array.isArray(user?.roles)
      ? user.roles
      : user?.role
      ? [user.role]
      : [];
  }, [user]);
};

// Хук для управления вкладками
const useTabNavigation = (
  activeTab,
  currentTabId,
  fetchGuidesByTab,
  fetchAvailableGuides
) => {
  useEffect(() => {
    if (
      activeTab !== null &&
      activeTab !== undefined &&
      typeof activeTab === 'number'
    ) {
      const tabId = tabIndexToId(activeTab);

      if (tabId && tabId !== currentTabId) {
        console.log(
          `Преобразование activeTab ${activeTab} в ID вкладки: ${tabId}`
        );
        fetchGuidesByTab(tabId);
      }
    } else if (!activeTab) {
      fetchAvailableGuides();
    }
  }, [activeTab, currentTabId, fetchGuidesByTab, fetchAvailableGuides]);

  return { tabId: activeTab !== null ? tabIndexToId(activeTab) : null };
};

// Хук для управления скроллом
const useScrollTop = () => {
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      const shouldShow = scrollPosition > 300;

      if (shouldShow !== showScrollTopButton) {
        setShowScrollTopButton(shouldShow);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Проверяем позицию скролла при монтировании

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showScrollTopButton]);

  return { showScrollTopButton, scrollToTop };
};

// Компонент индикатора ожидания действия
const ActionAwaitingIndicator = ({ isVisible, message }) => {
  if (!isVisible) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 16px',
        backgroundColor: '#3f51b5',
        color: 'white',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        zIndex: 10000,
      }}
    >
      {message}
    </div>,
    document.body
  );
};

// Компонент для отображения контента онбординга
const OnboardingContent = ({
  isOnboardingActive,
  currentStepData,
  isModalStep,
  targetElement,
  position,
  placement,
  title,
  content,
  handleNextStep,
  handlePrevStep,
  handleCompleteGuide,
  visibleStepIndex,
  totalSteps,
  isAwaitingAction,
  continuationMessage,
}) => {
  if (!isOnboardingActive) return null;

  // Для модальных шагов используем Dialog
  if (isModalStep) {
    return createPortal(
      <TutorialDialog
        open={true}
        position={position}
        title={title}
        content={content}
        placement={placement}
        onNext={handleNextStep}
        onPrev={handlePrevStep}
        onSkip={handleCompleteGuide}
        showPrev={visibleStepIndex > 0}
        isLastStep={visibleStepIndex === totalSteps - 1}
        currentStep={visibleStepIndex}
        totalSteps={totalSteps}
        isAwaitingAction={isAwaitingAction}
        continuationMessage={continuationMessage}
      />,
      document.body
    );
  }

  // Для обычных шагов используем Popper и Overlay
  return createPortal(
    <>
      <OnboardingOverlay targetElement={targetElement} />
      <TutorialPopper
        open={true}
        anchorEl={targetElement}
        title={title}
        content={content}
        placement={placement}
        onNext={handleNextStep}
        onPrev={handlePrevStep}
        onSkip={handleCompleteGuide}
        showPrev={visibleStepIndex > 0}
        isLastStep={visibleStepIndex === totalSteps - 1}
        currentStep={visibleStepIndex}
        totalSteps={totalSteps}
        isAwaitingAction={isAwaitingAction}
        continuationMessage={continuationMessage}
      />
    </>,
    document.body
  );
};

/**
 * Компонент для отображения онбординга
 *
 * @param {Object} props - свойства компонента
 * @param {number} props.activeTab - Индекс активной вкладки (если есть)
 */
const Onboarding = ({ activeTab = null }) => {
  const { user } = useAuth();

  const {
    isOnboardingActive,
    activeGuide,
    nextStep,
    restartOnboarding,
    availableGuides,
    fetchAvailableGuides,
    fetchGuidesByTab,
    currentTabId,
    isLoading,
    isProgressLoaded,
    checkStepDisplayConditions,
    completeOnboarding,
    isGuideCompleted,
    getAutostartGuide: contextGetAutostartGuide,
    checkGuideRolesAccess,
    checkStepUIRequirements,
  } = useOnboarding();

  // Используем хуки для управления скроллом и получения ролей пользователя
  const { showScrollTopButton, scrollToTop } = useScrollTop();
  const getUserRoles = useUserRoles(user);

  // Используем хук для управления вкладками
  useTabNavigation(
    activeTab,
    currentTabId,
    fetchGuidesByTab,
    fetchAvailableGuides
  );

  // Локальные состояния компонента
  const [isAwaitingAction, setIsAwaitingAction] = useState(false);
  const [continuationMessage, setContinuationMessage] = useState('');
  const [visibleStepIndex, setVisibleStepIndex] = useState(0);

  // Локальный флаг для предотвращения повторного автозапуска только что завершённого гида
  const justCompletedGuideId = React.useRef(null);

  // Проверка соответствия шага ролям пользователя
  const checkStepRoleAccess = useCallback(
    (step) => {
      // Если массив ролей не указан или пуст, шаг доступен всем
      if (!step || !step.roles || step.roles.length === 0) {
        return true;
      }

      const userRoles = getUserRoles();

      // Если у пользователя нет ролей, но у шага они указаны - не показываем
      if (userRoles.length === 0) {
        return false;
      }

      return userRoles.some((role) => step.roles.includes(role));
    },
    [getUserRoles]
  );

  // Используем хук для работы с видимыми шагами
  const { visibleSteps, shouldShowStep, getVisibleSteps } = useVisibleSteps(
    activeGuide,
    checkStepRoleAccess,
    checkStepDisplayConditions
  );

  const totalSteps = visibleSteps.length;
  const currentStepData = visibleSteps[visibleStepIndex] || null;

  // Проверка требуемого состояния UI перед показом шага
  useEffect(() => {
    if (currentStepData) {
      // Используем новый метод из контекста для управления UI
      checkStepUIRequirements(currentStepData);
    }
  }, [currentStepData, checkStepUIRequirements]);

  // Используем хук для управления условиями шага
  const {
    targetElement,
    position,
    placement,
    isModalStep,
    setupContinuationHandler,
  } = useOnboardingStep(currentStepData, isOnboardingActive, scrollToTop);

  // Функция для выбора подходящего гайда для автозапуска
  const findEligibleGuide = useCallback(() => {
    const userRoles = getUserRoles();
    return contextGetAutostartGuide(availableGuides, userRoles);
  }, [availableGuides, getUserRoles, contextGetAutostartGuide]);

  // Обработчик завершения гида
  const handleCompleteGuide = useCallback(async () => {
    if (!activeGuide) {
      setVisibleStepIndex(0);
      return;
    }

    // Устанавливаем флаг завершенного гайда перед запросом к серверу
    const guideIdToComplete = activeGuide._id;
    justCompletedGuideId.current = guideIdToComplete;

    try {
      // Отправляем запрос на сервер
      const response = await completeOnboarding(guideIdToComplete);

      if (!response?.success) {
        // В случае ошибки сбрасываем флаг
        console.error(
          'Ошибка при завершении гида:',
          response?.error || 'Неизвестная ошибка'
        );
        justCompletedGuideId.current = null;
      } else {
        console.log('Гид успешно отмечен как завершенный:', guideIdToComplete);
      }
    } catch (err) {
      console.error('Ошибка при завершении гида онбординга:', err);
      justCompletedGuideId.current = null;
    } finally {
      setVisibleStepIndex(0);
    }
  }, [activeGuide, completeOnboarding]);

  // Обработчик перехода к следующему шагу
  const handleNextStep = useCallback(() => {
    if (!activeGuide || !Array.isArray(activeGuide.steps)) {
      console.warn('Невозможно перейти к следующему шагу: гид не определен');
      return;
    }

    const visibleSteps = getVisibleSteps();
    if (!Array.isArray(visibleSteps) || visibleSteps.length === 0) {
      console.warn('Нет видимых шагов для перехода');
      handleCompleteGuide();
      return;
    }

    // Если текущий шаг последний, завершаем гайд
    if (visibleStepIndex >= visibleSteps.length - 1) {
      console.log(`Достигнут последний шаг ${visibleStepIndex}, завершаем гид`);
      handleCompleteGuide();
      return;
    }

    // Переходим к следующему видимому шагу
    console.log(
      `Переход к следующему шагу: ${visibleStepIndex} -> ${
        visibleStepIndex + 1
      }`
    );
    setVisibleStepIndex(visibleStepIndex + 1);

    // Прокручиваем страницу наверх, если нужно
    if (showScrollTopButton) {
      scrollToTop();
    }
  }, [
    activeGuide,
    visibleStepIndex,
    getVisibleSteps,
    handleCompleteGuide,
    showScrollTopButton,
    scrollToTop,
  ]);

  // Переход к предыдущему видимому шагу
  const handlePrevStep = useCallback(() => {
    if (visibleStepIndex > 0) {
      setVisibleStepIndex(visibleStepIndex - 1);
    }
  }, [visibleStepIndex]);

  // Настройка обработчика условий продолжения
  useEffect(() => {
    if (
      isOnboardingActive &&
      currentStepData &&
      currentStepData.continuationConditions &&
      currentStepData.continuationConditions.enabled
    ) {
      console.log(
        'Настраиваем обработчик условий продолжения:',
        currentStepData.continuationConditions
      );

      setIsAwaitingAction(true);
      setContinuationMessage(
        currentStepData.continuationConditions.message ||
          'Выполните действие для продолжения'
      );

      // Настраиваем обработчик событий для условия продолжения
      const cleanupHandler = setupContinuationHandler(currentStepData, () => {
        console.log(
          'Обработчик условия продолжения вызван, переходим к следующему шагу'
        );
        setIsAwaitingAction(false);

        // Переходим к следующему шагу с небольшой задержкой
        setTimeout(() => handleNextStep(), 300);
      });

      return () => {
        console.log('Очистка обработчика условий продолжения');
        if (cleanupHandler) cleanupHandler();
      };
    } else {
      setIsAwaitingAction(false);
    }
  }, [
    isOnboardingActive,
    currentStepData,
    setupContinuationHandler,
    handleNextStep,
  ]);

  // Автозапуск подходящего гида
  useEffect(() => {
    // Защита от повторных вызовов эффекта
    let isMounted = true;

    const autoStartGuide = async () => {
      // Проверяем необходимые условия для запуска
      if (
        isLoading ||
        !isProgressLoaded ||
        !Array.isArray(availableGuides) ||
        availableGuides.length === 0 ||
        isOnboardingActive
      ) {
        return;
      }
      
      // Ищем подходящий гайд для автозапуска
      const guideToStart = findEligibleGuide();

      // Если нет подходящих гидов или гид только что был завершен - пропускаем запуск
      if (!guideToStart) {
        return;
      }

      if (justCompletedGuideId.current === guideToStart._id) {
        return;
      }

      // Проверяем завершенность
      const isCompleted = isGuideCompleted(guideToStart._id);
      if (isCompleted) {
        return;
      }

      // Запускаем гид
      if (isMounted) {
        scrollToTop();
        await restartOnboarding(guideToStart._id);
      }
    };

    // Запускаем функцию асинхронно с задержкой для предотвращения race conditions
    const timeoutId = setTimeout(() => {
      autoStartGuide();
    }, 800);

    // Функция очистки для предотвращения обновления состояния после размонтирования
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [
    availableGuides,
    isOnboardingActive,
    findEligibleGuide,
    restartOnboarding,
    scrollToTop,
    isLoading,
    isProgressLoaded,
    isGuideCompleted,
  ]);

  // Обновление прогресса пользователя
  useEffect(() => {
    const updateUserProgress = async () => {
      if (isOnboardingActive && activeGuide && visibleStepIndex >= 0) {
        try {
          await onboardingAPI.updateProgress(activeGuide._id, visibleStepIndex);
        } catch (error) {
          console.error('Ошибка при обновлении прогресса:', error);
        }
      }
    };

    updateUserProgress();
  }, [isOnboardingActive, activeGuide, visibleStepIndex]);

  // Эффект для обработки вкладок с учетом текущего шага
  useEffect(() => {
    // Пропускаем эффект, если онбординг не активен или нет условий продолжения
    if (
      !isOnboardingActive ||
      !currentStepData ||
      !currentStepData.continuationConditions ||
      !currentStepData.continuationConditions.enabled
    )
      return;

    const selector = currentStepData.continuationConditions.elementSelector;
    if (!selector || !selector.includes('[role="tab"]')) return;

    console.log(
      'Проверка условий продолжения для вкладок, activeTab:',
      activeTab
    );

    // Стандартная логика для обработки вкладок
    const dataTabIdMatch = selector.match(/\[data-tab-id="([^"]+)"\]/);
    const nthChildMatch = selector.match(/nth-child\((\d+)\)/);

    // Функция для перехода к следующему шагу с задержкой
    const proceedToNextStep = () => {
      setIsAwaitingAction(false);
      setTimeout(() => handleNextStep(), 500);
    };

    // Проверка по data-tab-id
    if (dataTabIdMatch && dataTabIdMatch[1]) {
      const expectedTabId = dataTabIdMatch[1];
      const allTabs = document.querySelectorAll('[role="tab"][data-tab-id]');

      for (let i = 0; i < allTabs.length; i++) {
        if (
          allTabs[i].getAttribute('data-tab-id') === expectedTabId &&
          activeTab === i
        ) {
          console.log('activeTab соответствует вкладке с ID', expectedTabId);
          proceedToNextStep();
          return;
        }
      }
    }
    // Проверка по nth-child
    else if (nthChildMatch && nthChildMatch[1]) {
      const expectedTabIndex = parseInt(nthChildMatch[1]) - 1;
      if (activeTab === expectedTabIndex) {
        console.log(
          'activeTab соответствует ожидаемой вкладке, переходим к следующему шагу'
        );
        proceedToNextStep();
        return;
      }
    }

    // Подсветка нужной вкладки
    const highlightExpectedTab = () => {
      const tabElement = findElementBySelectors(selector);
      if (tabElement) {
        tabElement.classList.add('onboarding-highlight');
        tabElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Если не нашли по селектору, пробуем найти по индексу
      const allTabs = document.querySelectorAll('[role="tab"]');
      const expectedTabIndex = nthChildMatch
        ? parseInt(nthChildMatch[1]) - 1
        : null;
      if (
        expectedTabIndex !== null &&
        allTabs &&
        allTabs.length > expectedTabIndex
      ) {
        allTabs[expectedTabIndex].classList.add('onboarding-highlight');
        allTabs[expectedTabIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    };

    highlightExpectedTab();

    // Таймаут для автоматического перехода
    const timeoutId = setTimeout(() => {
      console.log('Таймаут автоперехода сработал');
      proceedToNextStep();
    }, 10000);

    return () => {
      clearTimeout(timeoutId);
      document.querySelectorAll('.onboarding-highlight').forEach((el) => {
        el.classList.remove('onboarding-highlight');
      });
    };
  }, [activeTab, isOnboardingActive, currentStepData, handleNextStep]);

  // Проверяем, есть ли доступные гиды для текущего пользователя
  const hasAvailableGuides = useCallback(() => {
    // Проверка на пустоту массива гидов
    if (
      !availableGuides ||
      !Array.isArray(availableGuides) ||
      availableGuides.length === 0
    ) {
      return false;
    }

    const userRoles = getUserRoles();

    // Проверяем наличие хотя бы одного гида, доступного по ролям
    return availableGuides.some(
      (guide) => guide && checkGuideRolesAccess(guide, userRoles)
    );
  }, [availableGuides, getUserRoles, checkGuideRolesAccess]);

  // Обновлённая версия: запускает гид только если он не завершен
  const handleStartOnboarding = useCallback(() => {
    if (!hasAvailableGuides()) return;

    const userRoles = getUserRoles();

    // Находим первый доступный и незавершенный гид
    const availableGuide = availableGuides.find(
      (guide) =>
        guide &&
        !isGuideCompleted(guide._id) &&
        checkGuideRolesAccess(guide, userRoles)
    );

    if (availableGuide) {
      console.log('Запуск незавершенного гида:', availableGuide.name);
      setVisibleStepIndex(0);
      scrollToTop();
      restartOnboarding(availableGuide._id);
    } else {
      // Если незавершенных гидов нет, берем первый доступный
      const firstGuide = availableGuides[0];
      if (firstGuide) {
        console.log('Запуск гида (все гиды завершены):', firstGuide.name);
        setVisibleStepIndex(0);
        scrollToTop();
        restartOnboarding(firstGuide._id);
      }
    }
  }, [
    availableGuides,
    restartOnboarding,
    scrollToTop,
    hasAvailableGuides,
    getUserRoles,
    isGuideCompleted,
    checkGuideRolesAccess,
  ]);

  // Перезапуск онбординга
  const handleRestartOnboarding = useCallback(async () => {
    if (!activeGuide && (!availableGuides || availableGuides.length === 0)) {
      console.warn('Нет доступных гидов для перезапуска');
      return;
    }

    const guideId = activeGuide?._id || availableGuides[0]._id;

    try {
      setVisibleStepIndex(0); // Сброс шага при рестарте
      await onboardingAPI.restartGuide(guideId);
      scrollToTop();
      restartOnboarding(guideId);
    } catch (err) {
      console.error('Ошибка при перезапуске гида онбординга:', err);
    }
  }, [activeGuide, availableGuides, restartOnboarding, scrollToTop]);

  // Получаем контент для текущего шага
  const getStepContent = useCallback(() => {
    if (
      !activeGuide ||
      !activeGuide.steps ||
      visibleStepIndex >= activeGuide.steps.length
    ) {
      return { title: '', content: '' };
    }

    const step = activeGuide.steps[visibleStepIndex];
    return {
      title: step.title || '',
      content: step.content || '',
    };
  }, [activeGuide, visibleStepIndex]);

  // Получаем статус завершённости первого доступного гида для отображения кнопок
  const availableGuideForButtons =
    availableGuides && availableGuides.length > 0 ? availableGuides[0] : null;
  const isFirstGuideCompletedForButtons =
    availableGuideForButtons && isGuideCompleted(availableGuideForButtons._id);

  // Проверка видимости текущего шага и автоматический переход, если шаг не видим
  useEffect(() => {
    if (
      isOnboardingActive &&
      currentStepData &&
      !shouldShowStep(currentStepData)
    ) {
      // Если шаг не соответствует условиям, автоматически переходим к следующему
      const timeoutId = setTimeout(() => nextStep(), 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isOnboardingActive, currentStepData, shouldShowStep, nextStep]);

  // Если идет загрузка гидов, не показываем ничего
  if (isLoading) return null;

  // Получаем контент для текущего шага
  const { title, content } = getStepContent();

  // Главный рендер компонента
  return (
    <>
      {/* Кнопки управления туториалом */}
      {createPortal(
        <TutorialControls
          showHelpButton={
            Array.isArray(availableGuides) && availableGuides.length > 0
          }
          showScrollTopButton={showScrollTopButton}
          isOnboardingActive={isOnboardingActive}
          isGuideCompleted={isFirstGuideCompletedForButtons}
          onHelp={handleStartOnboarding}
          onRestart={handleRestartOnboarding}
          onScrollTop={scrollToTop}
          availableGuides={availableGuides}
          onSelectGuide={(guideId) => {
            setVisibleStepIndex(0);
            scrollToTop();
            restartOnboarding(guideId);
          }}
        />,
        document.body
      )}

      {/* Содержимое туториала */}
      {isOnboardingActive && (
        <OnboardingContent
          isOnboardingActive={isOnboardingActive}
          currentStepData={currentStepData}
          isModalStep={isModalStep}
          targetElement={targetElement}
          position={position}
          placement={placement}
          title={title}
          content={content}
          handleNextStep={handleNextStep}
          handlePrevStep={handlePrevStep}
          handleCompleteGuide={handleCompleteGuide}
          visibleStepIndex={visibleStepIndex}
          totalSteps={totalSteps}
          isAwaitingAction={isAwaitingAction}
          continuationMessage={continuationMessage}
        />
      )}

      {/* Индикатор ожидания действия - добавляем только если не отображается в диалоге/поппере */}
      <ActionAwaitingIndicator
        isVisible={
          isOnboardingActive &&
          isAwaitingAction &&
          !targetElement &&
          !isModalStep
        }
        message={continuationMessage}
      />
    </>
  );
};

export default Onboarding;
